import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const ROLES = [
  { key: 'owner', label: 'Owner', description: 'Full platform access', color: 'bg-purple-100 text-purple-700', icon: 'Crown' },
  { key: 'admin', label: 'Admin', description: 'Full access except billing', color: 'bg-blue-100 text-blue-700', icon: 'ShieldCheck' },
  { key: 'manager', label: 'Manager', description: 'Operations & limited finance', color: 'bg-indigo-100 text-indigo-700', icon: 'Briefcase' },
  { key: 'technician', label: 'Technician', description: 'Field operations only', color: 'bg-green-100 text-green-700', icon: 'Wrench' },
  { key: 'salesperson', label: 'Salesperson', description: 'Sales & customer management', color: 'bg-orange-100 text-orange-700', icon: 'TrendingUp' },
  { key: 'viewer', label: 'Viewer', description: 'Read-only access', color: 'bg-gray-100 text-gray-600', icon: 'Eye' },
];

const PERMISSION_SECTIONS = [
  {
    id: 'navigation',
    label: 'Navigation & Menus',
    icon: 'LayoutDashboard',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    permissions: [
      { key: 'can_view_dashboard', label: 'Dashboard', description: 'Main org dashboard' },
      { key: 'can_view_my_dashboard', label: 'My Dashboard', description: 'Personal dashboard view' },
      { key: 'can_view_sales', label: 'Sales Menu', description: 'Quotes, invoices, customers' },
      { key: 'can_view_operations', label: 'Operations Menu', description: 'Tickets, assignments, technicians' },
      { key: 'can_view_finance', label: 'Finance Menu', description: 'Billing, payments, accounting' },
      { key: 'can_view_reports', label: 'Reports Menu', description: 'Analytics and reporting' },
      { key: 'can_view_data_management', label: 'Data Management', description: 'Import/export data' },
      { key: 'can_view_settings', label: 'Settings Menu', description: 'Org settings and configuration' },
    ],
  },
  {
    id: 'tickets',
    label: 'Ticket Management',
    icon: 'ClipboardList',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    permissions: [
      { key: 'can_create_tickets', label: 'Create Tickets', description: 'Open new service tickets' },
      { key: 'can_edit_tickets', label: 'Edit Tickets', description: 'Modify existing tickets' },
      { key: 'can_delete_tickets', label: 'Delete Tickets', description: 'Remove tickets permanently' },
      { key: 'can_assign_tickets', label: 'Assign Tickets', description: 'Assign tickets to technicians' },
      { key: 'can_close_tickets', label: 'Close Tickets', description: 'Mark tickets as resolved' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales & Finance',
    icon: 'DollarSign',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    permissions: [
      { key: 'can_create_quotes', label: 'Create Quotes', description: 'Draft new price quotes' },
      { key: 'can_approve_quotes', label: 'Approve Quotes', description: 'Authorize quote approval' },
      { key: 'can_create_invoices', label: 'Create Invoices', description: 'Generate invoices' },
      { key: 'can_manage_billing', label: 'Manage Billing', description: 'Access billing & subscriptions' },
      { key: 'can_view_financials', label: 'View Financials', description: 'See revenue and payment data' },
    ],
  },
  {
    id: 'personnel',
    label: 'Personnel & Access',
    icon: 'Users',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    permissions: [
      { key: 'can_manage_personnel', label: 'Manage Personnel', description: 'Add, edit, remove staff' },
      { key: 'can_manage_customers', label: 'Manage Customers', description: 'Customer database access' },
      { key: 'can_manage_inventory', label: 'Manage Inventory', description: 'Inventory tracking & updates' },
      { key: 'can_send_invites', label: 'Send Invitations', description: 'Invite users to the platform' },
      { key: 'can_manage_roles', label: 'Manage Roles', description: 'Assign and change user roles' },
    ],
  },
  {
    id: 'system',
    label: 'System & Security',
    icon: 'Shield',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    permissions: [
      { key: 'can_view_audit_logs', label: 'View Audit Logs', description: 'Access security audit trail' },
      { key: 'can_export_data', label: 'Export Data', description: 'Download reports and data' },
      { key: 'can_manage_integrations', label: 'Manage Integrations', description: 'Configure third-party integrations' },
      { key: 'can_manage_org_settings', label: 'Org Settings', description: 'Modify organization settings' },
    ],
  },
];

const DEFAULT_PERMISSIONS = {
  owner: { can_view_dashboard: true, can_view_my_dashboard: true, can_view_sales: true, can_view_operations: true, can_view_finance: true, can_view_reports: true, can_view_data_management: true, can_view_settings: true, can_create_tickets: true, can_edit_tickets: true, can_delete_tickets: true, can_assign_tickets: true, can_close_tickets: true, can_create_quotes: true, can_approve_quotes: true, can_create_invoices: true, can_manage_billing: true, can_view_financials: true, can_manage_personnel: true, can_manage_customers: true, can_manage_inventory: true, can_send_invites: true, can_manage_roles: true, can_view_audit_logs: true, can_export_data: true, can_manage_integrations: true, can_manage_org_settings: true },
  admin: { can_view_dashboard: true, can_view_my_dashboard: true, can_view_sales: true, can_view_operations: true, can_view_finance: true, can_view_reports: true, can_view_data_management: true, can_view_settings: true, can_create_tickets: true, can_edit_tickets: true, can_delete_tickets: true, can_assign_tickets: true, can_close_tickets: true, can_create_quotes: true, can_approve_quotes: true, can_create_invoices: true, can_manage_billing: false, can_view_financials: true, can_manage_personnel: true, can_manage_customers: true, can_manage_inventory: true, can_send_invites: true, can_manage_roles: true, can_view_audit_logs: true, can_export_data: true, can_manage_integrations: true, can_manage_org_settings: true },
  manager: { can_view_dashboard: true, can_view_my_dashboard: true, can_view_sales: true, can_view_operations: true, can_view_finance: false, can_view_reports: true, can_view_data_management: false, can_view_settings: false, can_create_tickets: true, can_edit_tickets: true, can_delete_tickets: false, can_assign_tickets: true, can_close_tickets: true, can_create_quotes: true, can_approve_quotes: false, can_create_invoices: true, can_manage_billing: false, can_view_financials: false, can_manage_personnel: true, can_manage_customers: true, can_manage_inventory: true, can_send_invites: false, can_manage_roles: false, can_view_audit_logs: false, can_export_data: true, can_manage_integrations: false, can_manage_org_settings: false },
  technician: { can_view_dashboard: false, can_view_my_dashboard: true, can_view_sales: false, can_view_operations: true, can_view_finance: false, can_view_reports: false, can_view_data_management: false, can_view_settings: false, can_create_tickets: true, can_edit_tickets: true, can_delete_tickets: false, can_assign_tickets: false, can_close_tickets: true, can_create_quotes: false, can_approve_quotes: false, can_create_invoices: false, can_manage_billing: false, can_view_financials: false, can_manage_personnel: false, can_manage_customers: false, can_manage_inventory: false, can_send_invites: false, can_manage_roles: false, can_view_audit_logs: false, can_export_data: false, can_manage_integrations: false, can_manage_org_settings: false },
  salesperson: { can_view_dashboard: false, can_view_my_dashboard: true, can_view_sales: true, can_view_operations: false, can_view_finance: false, can_view_reports: true, can_view_data_management: false, can_view_settings: false, can_create_tickets: false, can_edit_tickets: false, can_delete_tickets: false, can_assign_tickets: false, can_close_tickets: false, can_create_quotes: true, can_approve_quotes: false, can_create_invoices: true, can_manage_billing: false, can_view_financials: false, can_manage_personnel: false, can_manage_customers: true, can_manage_inventory: false, can_send_invites: false, can_manage_roles: false, can_view_audit_logs: false, can_export_data: true, can_manage_integrations: false, can_manage_org_settings: false },
  viewer: { can_view_dashboard: true, can_view_my_dashboard: true, can_view_sales: false, can_view_operations: false, can_view_finance: false, can_view_reports: false, can_view_data_management: false, can_view_settings: false, can_create_tickets: false, can_edit_tickets: false, can_delete_tickets: false, can_assign_tickets: false, can_close_tickets: false, can_create_quotes: false, can_approve_quotes: false, can_create_invoices: false, can_manage_billing: false, can_view_financials: false, can_manage_personnel: false, can_manage_customers: false, can_manage_inventory: false, can_send_invites: false, can_manage_roles: false, can_view_audit_logs: false, can_export_data: false, can_manage_integrations: false, can_manage_org_settings: false },
};

const PermissionManagement = () => {
  const { organizationId, isOrgAdmin, isSuperAdmin } = useAuth();
  const [activeRole, setActiveRole] = useState('manager');
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedRole, setSavedRole] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState(PERMISSION_SECTIONS?.map(s => s?.id));
  const [compareMode, setCompareMode] = useState(false);
  const [compareRole, setCompareRole] = useState('admin');
  const [searchQuery, setSearchQuery] = useState('');
  const [unsavedChanges, setUnsavedChanges] = useState({});

  useEffect(() => {
    if (organizationId) fetchPermissions();
  }, [organizationId]);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const { data, error: err } = await supabase?.from('role_permissions')?.select('*')?.eq('organization_id', organizationId);
      if (err) throw err;
      const permMap = {};
      data?.forEach(p => { permMap[p.role] = p; });
      // Merge with defaults for any missing roles
      ROLES?.forEach(r => {
        if (!permMap?.[r?.key]) permMap[r.key] = { ...DEFAULT_PERMISSIONS?.[r?.key] };
      });
      setPermissions(permMap);
    } catch (err) {
      setError(err?.message);
      // Use defaults on error
      const defaults = {};
      ROLES?.forEach(r => { defaults[r.key] = { ...DEFAULT_PERMISSIONS?.[r?.key] }; });
      setPermissions(defaults);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (role, permKey) => {
    if (role === 'owner' || role === 'admin') return;
    setPermissions(prev => ({
      ...prev,
      [role]: { ...prev?.[role], [permKey]: !prev?.[role]?.[permKey] },
    }));
    setUnsavedChanges(prev => ({ ...prev, [role]: true }));
  };

  const handleSave = async (role) => {
    if (!organizationId) return;
    setSaving(true);
    setError(null);
    try {
      const rolePerms = permissions?.[role];
      const { error: err } = await supabase?.from('role_permissions')?.upsert(
        { ...rolePerms, organization_id: organizationId, role },
        { onConflict: 'organization_id,role' }
      );
      if (err) throw err;
      setSavedRole(role);
      setUnsavedChanges(prev => ({ ...prev, [role]: false }));
      setTimeout(() => setSavedRole(null), 2500);
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = (role) => {
    if (role === 'owner' || role === 'admin') return;
    setPermissions(prev => ({
      ...prev,
      [role]: { ...DEFAULT_PERMISSIONS?.[role] },
    }));
    setUnsavedChanges(prev => ({ ...prev, [role]: true }));
  };

  const handleCopyFrom = (fromRole, toRole) => {
    if (toRole === 'owner' || toRole === 'admin') return;
    setPermissions(prev => ({
      ...prev,
      [toRole]: { ...prev?.[fromRole] },
    }));
    setUnsavedChanges(prev => ({ ...prev, [toRole]: true }));
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev?.includes(sectionId) ? prev?.filter(s => s !== sectionId) : [...prev, sectionId]
    );
  };

  const getPermissionCount = (role) => {
    const perms = permissions?.[role] || {};
    const total = Object.keys(perms)?.filter(k => k?.startsWith('can_'))?.length;
    const enabled = Object.entries(perms)?.filter(([k, v]) => k?.startsWith('can_') && v)?.length;
    return { enabled, total };
  };

  const filteredSections = PERMISSION_SECTIONS?.map(section => ({
    ...section,
    permissions: section?.permissions?.filter(p =>
      !searchQuery || p?.label?.toLowerCase()?.includes(searchQuery?.toLowerCase()) || p?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    ),
  }))?.filter(s => !searchQuery || s?.permissions?.length > 0);

  const currentPerms = permissions?.[activeRole] ?? {};
  const comparePerms = permissions?.[compareRole] ?? {};
  const isReadOnly = activeRole === 'owner' || activeRole === 'admin';
  const canEdit = isOrgAdmin || isSuperAdmin;

  return (
    <>
      <Helmet><title>Permission Management - FieldServi</title></Helmet>
      <Header />
      <main className="pt-16 min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Icon name="ShieldCheck" size={18} color="#2563eb" />
                </div>
                <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Access Control</span>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Permission Management</h1>
              <p className="text-muted-foreground text-sm mt-1">Configure granular access controls and role-based permissions for your organization</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCompareMode(!compareMode)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-smooth ${
                  compareMode ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="GitCompare" size={14} color="currentColor" />
                Compare Roles
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <Icon name="AlertCircle" size={14} color="currentColor" />
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="flex gap-6">
              {/* Role Sidebar */}
              <div className="w-56 flex-shrink-0">
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  <div className="px-4 py-3 border-b border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Roles</p>
                  </div>
                  {ROLES?.map(role => {
                    const { enabled, total } = getPermissionCount(role?.key);
                    const pct = total > 0 ? Math.round((enabled / total) * 100) : 0;
                    return (
                      <button
                        key={role?.key}
                        onClick={() => setActiveRole(role?.key)}
                        className={`w-full text-left px-4 py-3 border-b border-border last:border-0 transition-smooth group ${
                          activeRole === role?.key ? 'bg-primary/8 border-l-2 border-l-primary' : 'hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${role?.color}`}>
                            <Icon name={role?.icon} size={12} color="currentColor" />
                          </div>
                          <span className={`text-sm font-medium ${
                            activeRole === role?.key ? 'text-primary' : 'text-foreground'
                          }`}>{role?.label}</span>
                          {unsavedChanges?.[role?.key] && (
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0 ml-auto"></span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary/60 rounded-full transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">{enabled}/{total}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Copy Permissions */}
                {!isReadOnly && canEdit && (
                  <div className="mt-4 bg-card rounded-xl border border-border p-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Copy From Role</p>
                    <div className="space-y-1.5">
                      {ROLES?.filter(r => r?.key !== activeRole)?.map(r => (
                        <button
                          key={r?.key}
                          onClick={() => handleCopyFrom(r?.key, activeRole)}
                          className="w-full text-left px-3 py-2 rounded-lg text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth flex items-center gap-2"
                        >
                          <Icon name="Copy" size={11} color="currentColor" />
                          Copy from {r?.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Main Panel */}
              <div className="flex-1 min-w-0">
                {/* Role Header */}
                <div className="bg-card rounded-xl border border-border p-5 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const role = ROLES?.find(r => r?.key === activeRole);
                        return (
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${role?.color}`}>
                            <Icon name={role?.icon || 'User'} size={20} color="currentColor" />
                          </div>
                        );
                      })()}
                      <div>
                        <h2 className="text-lg font-heading font-semibold text-foreground">
                          {ROLES?.find(r => r?.key === activeRole)?.label} Permissions
                        </h2>
                        <p className="text-sm text-muted-foreground">{ROLES?.find(r => r?.key === activeRole)?.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isReadOnly && canEdit && (
                        <>
                          <button
                            onClick={() => handleResetToDefaults(activeRole)}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-muted-foreground border border-border hover:bg-muted transition-smooth"
                          >
                            <Icon name="RotateCcw" size={13} color="currentColor" />
                            Reset
                          </button>
                          <button
                            onClick={() => handleSave(activeRole)}
                            disabled={saving}
                            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50"
                          >
                            {savedRole === activeRole ? (
                              <><Icon name="Check" size={14} color="currentColor" /> Saved!</>
                            ) : (
                              <><Icon name="Save" size={14} color="currentColor" /> Save Changes</>
                            )}
                          </button>
                        </>
                      )}
                      {isReadOnly && (
                        <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                          Full Access — Cannot Restrict
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Search */}
                  <div className="mt-4 relative">
                    <Icon name="Search" size={14} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search permissions..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e?.target?.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                </div>

                {/* Compare Mode Banner */}
                {compareMode && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4 flex items-center gap-4">
                    <Icon name="GitCompare" size={16} color="#4f46e5" />
                    <span className="text-sm text-indigo-700 font-medium">Comparing with:</span>
                    <div className="flex gap-2">
                      {ROLES?.filter(r => r?.key !== activeRole)?.map(r => (
                        <button
                          key={r?.key}
                          onClick={() => setCompareRole(r?.key)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-smooth ${
                            compareRole === r?.key ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-100'
                          }`}
                        >
                          {r?.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Permission Sections */}
                {isReadOnly ? (
                  <div className="bg-card rounded-xl border border-border p-12 text-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Icon name="Crown" size={28} color="#7c3aed" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                      {ROLES?.find(r => r?.key === activeRole)?.label} has Full Access
                    </h3>
                    <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                      {activeRole === 'owner' ? 'Owners have unrestricted access to all features and cannot be limited.' : 'Admins have full access to all features except billing management.'}
                    </p>
                    <div className="mt-6 grid grid-cols-3 gap-3 max-w-md mx-auto">
                      {PERMISSION_SECTIONS?.map(s => (
                        <div key={s?.id} className={`${s?.bgColor} rounded-lg p-3 text-center`}>
                          <Icon name={s?.icon} size={16} color="currentColor" className={s?.color} />
                          <p className={`text-xs font-medium mt-1 ${s?.color}`}>{s?.label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">All enabled</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredSections?.map(section => (
                      <div key={section?.id} className="bg-card rounded-xl border border-border overflow-hidden">
                        <button
                          onClick={() => toggleSection(section?.id)}
                          className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-smooth"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 ${section?.bgColor} rounded-lg flex items-center justify-center`}>
                              <Icon name={section?.icon} size={15} color="currentColor" className={section?.color} />
                            </div>
                            <div className="text-left">
                              <span className="text-sm font-semibold text-foreground">{section?.label}</span>
                              <span className="ml-2 text-xs text-muted-foreground">
                                ({section?.permissions?.filter(p => currentPerms?.[p?.key])?.length}/{section?.permissions?.length} enabled)
                              </span>
                            </div>
                          </div>
                          <Icon
                            name={expandedSections?.includes(section?.id) ? 'ChevronUp' : 'ChevronDown'}
                            size={16}
                            color="var(--color-muted-foreground)"
                          />
                        </button>

                        {expandedSections?.includes(section?.id) && (
                          <div className="border-t border-border">
                            {compareMode ? (
                              <div className="grid grid-cols-2 divide-x divide-border">
                                <div className="p-4">
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                                    {ROLES?.find(r => r?.key === activeRole)?.label}
                                  </p>
                                  <div className="space-y-2">
                                    {section?.permissions?.map(perm => (
                                      <div key={perm?.key} className="flex items-center justify-between py-1.5">
                                        <span className="text-sm text-foreground">{perm?.label}</span>
                                        <div
                                          onClick={() => canEdit && handleToggle(activeRole, perm?.key)}
                                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                                            currentPerms?.[perm?.key] ? 'bg-primary' : 'bg-gray-200'
                                          } ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                                        >
                                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                            currentPerms?.[perm?.key] ? 'translate-x-4' : 'translate-x-1'
                                          }`} />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="p-4">
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                                    {ROLES?.find(r => r?.key === compareRole)?.label}
                                  </p>
                                  <div className="space-y-2">
                                    {section?.permissions?.map(perm => {
                                      const active = currentPerms?.[perm?.key];
                                      const compare = comparePerms?.[perm?.key];
                                      const diff = active !== compare;
                                      return (
                                        <div key={perm?.key} className={`flex items-center justify-between py-1.5 px-2 rounded ${
                                          diff ? 'bg-yellow-50' : ''
                                        }`}>
                                          <span className="text-sm text-foreground">{perm?.label}</span>
                                          <div className="flex items-center gap-2">
                                            {diff && <Icon name="ArrowLeftRight" size={11} color="#d97706" />}
                                            <div className={`relative inline-flex h-5 w-9 items-center rounded-full ${
                                              compare ? 'bg-primary/60' : 'bg-gray-200'
                                            } opacity-70`}>
                                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                                compare ? 'translate-x-4' : 'translate-x-1'
                                              }`} />
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {section?.permissions?.map(perm => (
                                  <label
                                    key={perm?.key}
                                    className={`flex items-center gap-3 p-3 rounded-lg border transition-smooth ${
                                      canEdit ? 'cursor-pointer hover:bg-muted/30' : 'cursor-default'
                                    } ${
                                      currentPerms?.[perm?.key] ? 'border-primary/20 bg-primary/3' : 'border-border'
                                    }`}
                                  >
                                    <div
                                      onClick={() => canEdit && handleToggle(activeRole, perm?.key)}
                                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
                                        currentPerms?.[perm?.key] ? 'bg-primary' : 'bg-gray-200'
                                      } ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                    >
                                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                        currentPerms?.[perm?.key] ? 'translate-x-4' : 'translate-x-1'
                                      }`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-foreground">{perm?.label}</p>
                                      <p className="text-xs text-muted-foreground truncate">{perm?.description}</p>
                                    </div>
                                    {currentPerms?.[perm?.key] ? (
                                      <Icon name="CheckCircle" size={14} color="var(--color-primary)" className="flex-shrink-0" />
                                    ) : (
                                      <Icon name="XCircle" size={14} color="var(--color-muted-foreground)" className="flex-shrink-0 opacity-40" />
                                    )}
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default PermissionManagement;
